import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { api } from '../services/api';
import { useAuth } from '../providers/AuthProvider';
import { PricingCarousel } from '../components/embed/PricingCarousel';
import { TokenCalculator } from '../components/embed/TokenCalculator';

type View = 'Plans' | 'Calculator';

export function PricingPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const promoParam = searchParams.get('promo');
  const [view, setView] = useState<View>('Plans');

  const { data: packages, isLoading, error } = useQuery({
    queryKey: ['packages'],
    queryFn: () => api.getPackages(),
    staleTime: 5 * 60 * 1000,
  });

  const promoSuffix = promoParam ? `&promo=${encodeURIComponent(promoParam)}` : '';

  const handleSelect = (slug: string) => {
    if (!isAuthenticated) {
      navigate(`/register?package=${encodeURIComponent(slug)}${promoSuffix}`);
      return;
    }
    navigate(`/dashboard/checkout?package=${slug}${promoSuffix}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <header className="border-b border-slate-800">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/vortex-logo-white.svg" alt="Vortex Marine" className="h-8" />
            <span className="text-xl font-bold text-white">Vortex Marine</span>
          </Link>
          <nav className="flex items-center gap-4">
            <Link to="/app" className="text-slate-300 hover:text-white transition-colors">
              Create Chart
            </Link>
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-400 transition-colors"
              >
                Dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-400 transition-colors"
              >
                Sign In
              </Link>
            )}
          </nav>
        </div>
      </header>

      <section className="py-12 text-center">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Simple, Transparent Pricing
        </h1>
        <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-2">
          Choose the plan that fits your navigation needs
        </p>
        <p className="text-sm text-slate-500 max-w-xl mx-auto">
          All plans are billed quarterly in advance — pay once every 3 months and get your full token allocation upfront.
        </p>
      </section>

      <section className="pb-8">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center text-red-400 py-16">
            Failed to load packages. Please try again later.
          </div>
        ) : packages && packages.length > 0 ? (
          <AnimatePresence mode="wait">
            {view === 'Plans' ? (
              <motion.div
                key="plans"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <PricingCarousel
                  packages={packages}
                  onSelect={handleSelect}
                  onSwitchView={() => setView('Calculator')}
                  selectLabel={isAuthenticated ? 'Select Plan' : 'Get Started'}
                />
              </motion.div>
            ) : (
              <motion.div
                key="calculator"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <TokenCalculator
                  onSelect={handleSelect}
                  onSwitchView={() => setView('Plans')}
                />
              </motion.div>
            )}
          </AnimatePresence>
        ) : (
          <div className="text-center text-slate-400 py-16">
            No packages available at this time.
          </div>
        )}
      </section>

      <section className="container mx-auto px-4 pb-16">
        <div className="max-w-4xl mx-auto bg-slate-800/50 rounded-2xl border border-slate-700 p-8">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">What are tokens?</h3>
              <p className="text-slate-400">
                Each token represents a single map tile at a specific zoom level. The number of tokens needed depends on your area of interest and zoom levels selected.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">What happens to unused tokens?</h3>
              <p className="text-slate-400">
                Tokens expire at the end of each quarterly billing period. Any unused tokens do not carry over to the next quarter, so make the most of your allocation each billing cycle.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Can I upgrade or downgrade my plan?</h3>
              <p className="text-slate-400">
                Absolutely. You can change your plan at any time. Upgrades take effect immediately, and downgrades take effect at the end of your billing period.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">What payment methods do you accept?</h3>
              <p className="text-slate-400">
                We accept all major credit cards (Visa, Mastercard, American Express), Google Pay, and Apple Pay through our secure payment provider, Stripe.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-800 py-8">
        <div className="container mx-auto px-4 text-center text-slate-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Vortex Marine. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default PricingPage;
